import { UnitDTO } from "shared/types/unit";

/**
 * Vacancy metrics shared between the home dashboard and the leegstand table.
 * Logic mirrors `homeNew.processUnits` so the leegstand numbers line up with
 * the dashboard exactly.
 */

export type VacancyReason =
  | "manual_override"
  | "renovation_completion"
  | "contract_end"
  | "since_acquisition";

export interface CurrentVacancyMetrics {
  vacantSince: Date | null;
  daysVacant: number;
  /** Rounded ERV × (daysVacant / 30.44) */
  lostRevenue: number;
  reason: VacancyReason;
  lastTenantNames: string;
}

export interface UpcomingVacancyMetrics {
  contractEndDate: Date | null;
  /** Days from now until contract end (>= 0). Null when no end date found. */
  daysUntilEnd: number | null;
}

const isEndDate = (type: string | undefined) =>
  type === "Einddatum" || type === "END_DATE";

/**
 * Resolve the most recent contract end date for a unit, plus the tenant names
 * on that contract. Mirrors the logic inside homeNew.processUnits so both
 * views agree.
 */
function resolveMostRecentContractEnd(unit: any, today: Date): {
  contractEndDate: Date | null;
  lastTenantNames: string;
} {
  const allContractsWithEndDates: Array<{ contract: any; endDate: Date }> = [];

  if (Array.isArray(unit?.contracts)) {
    for (const contract of unit.contracts) {
      const milestones = contract?.contractMilestoneDates;
      if (!Array.isArray(milestones) || milestones.length === 0) continue;

      const endDates = milestones
        .filter((m: any) => isEndDate(m?.milestoneDateType))
        .map((m: any) => (m?.date ? new Date(m.date) : null))
        .filter((d: Date | null): d is Date => {
          if (!d || Number.isNaN(d.getTime())) return false;
          // Gesloten contracts shouldn't pull in a stale "original term" end
          // date that lies in the future.
          if (contract.phase === "Gesloten") return d <= today;
          return true;
        });

      if (endDates.length > 0) {
        const mostRecent = endDates.sort((a, b) => b.getTime() - a.getTime())[0];
        allContractsWithEndDates.push({ contract, endDate: mostRecent });
      }
    }
  }

  // Fallback: end dates directly on the unit (legacy payloads)
  if (allContractsWithEndDates.length === 0 && Array.isArray((unit as any)?.contractMilestoneDates)) {
    const unitEndDates = (unit as any).contractMilestoneDates
      .filter((m: any) => isEndDate(m?.milestoneDateType))
      .map((m: any) => (m?.date ? new Date(m.date) : null))
      .filter((d: Date | null): d is Date => !!d && !Number.isNaN(d.getTime()));

    if (unitEndDates.length > 0) {
      const mostRecent = unitEndDates.sort(
        (a: Date, b: Date) => b.getTime() - a.getTime(),
      )[0];
      return { contractEndDate: mostRecent, lastTenantNames: "" };
    }
  }

  if (allContractsWithEndDates.length === 0) {
    return { contractEndDate: null, lastTenantNames: "" };
  }

  const mostRecent = allContractsWithEndDates.sort(
    (a, b) => b.endDate.getTime() - a.endDate.getTime(),
  )[0];

  let lastTenantNames = "";
  const tenantContacts = mostRecent.contract?.contractContacts;
  if (Array.isArray(tenantContacts)) {
    lastTenantNames = tenantContacts
      .filter((c: any) => c?.contactType === "Contractant (huurder)")
      .map((c: any) => c?.resource?.name)
      .filter(Boolean)
      .join(", ");
  }

  return { contractEndDate: mostRecent.endDate, lastTenantNames };
}

/**
 * "Leeg sinds" start = the most recent moment the unit re-entered the rental
 * cycle. In order of priority: renovatie-opleveringsdatum > laatste contract
 * einde > unitBuiltAt > building.acquisitionDate. Each is valid only when it
 * lies in the past — a future renovation completion doesn't retro-start the
 * vacancy timer. The helper takes the latest of whatever qualifies.
 */
export function getCurrentVacancyMetrics(
  unit: UnitDTO | any,
  now: Date = new Date(),
): CurrentVacancyMetrics {
  const today = startOfDay(now);
  const { contractEndDate, lastTenantNames } = resolveMostRecentContractEnd(unit, today);

  const overrideRaw = (unit as any)?.vacancyStartDateOverride;
  const overrideDate =
    overrideRaw && !Number.isNaN(new Date(overrideRaw).getTime())
      ? startOfDay(new Date(overrideRaw))
      : null;

  const renovationDateRaw = (unit as any)?.renovationCompletionDate;
  const renovationCompletion =
    renovationDateRaw && !Number.isNaN(new Date(renovationDateRaw).getTime())
      ? new Date(renovationDateRaw)
      : null;

  const builtAtRaw = (unit as any)?.unitBuiltAt;
  const unitBuiltAt =
    builtAtRaw && !Number.isNaN(new Date(builtAtRaw).getTime())
      ? new Date(builtAtRaw)
      : null;

  const acquisitionRaw = unit?.building?.acquisitionDate;
  const acquisitionDate =
    acquisitionRaw && !Number.isNaN(new Date(acquisitionRaw).getTime())
      ? new Date(acquisitionRaw)
      : null;

  // The "leeg sinds" benchmark is always the most recent event that ended the
  // active rental cycle. The manual override participates in this comparison
  // — it only takes precedence when it's later than every auto-derived event
  // (so a stale override earlier than an actual newer contract end is
  // ignored, and the tenant's real move-out date wins).
  type Candidate = { date: Date; reason: VacancyReason };
  const candidates: Candidate[] = [];
  if (overrideDate && overrideDate.getTime() <= today.getTime()) {
    candidates.push({ date: overrideDate, reason: "manual_override" });
  }
  if (renovationCompletion && renovationCompletion.getTime() <= today.getTime()) {
    candidates.push({ date: renovationCompletion, reason: "renovation_completion" });
  }
  if (contractEndDate && contractEndDate.getTime() <= today.getTime()) {
    candidates.push({ date: contractEndDate, reason: "contract_end" });
  }
  if (unitBuiltAt && unitBuiltAt.getTime() <= today.getTime()) {
    candidates.push({ date: unitBuiltAt, reason: "since_acquisition" });
  } else if (acquisitionDate && acquisitionDate.getTime() <= today.getTime()) {
    candidates.push({ date: acquisitionDate, reason: "since_acquisition" });
  }

  // Sort by date descending; for equal dates, prefer manual_override so the UI
  // keeps reflecting the user's intent when it matches the auto-derived date.
  const reasonPriority: Record<VacancyReason, number> = {
    manual_override: 3,
    renovation_completion: 2,
    contract_end: 1,
    since_acquisition: 0,
  };
  candidates.sort((a, b) => {
    const diff = b.date.getTime() - a.date.getTime();
    if (diff !== 0) return diff;
    return reasonPriority[b.reason] - reasonPriority[a.reason];
  });
  const best = candidates[0];

  let vacantSince: Date | null = best?.date ?? null;
  let reason: VacancyReason = best?.reason ?? "contract_end";

  let daysVacant = 0;
  let lostRevenue = 0;

  if (vacantSince) {
    daysVacant = Math.floor(
      (today.getTime() - vacantSince.getTime()) / (1000 * 60 * 60 * 24),
    );
    const monthsVacant = daysVacant / 30.44;
    lostRevenue = Math.round((unit.estimatedRentalValue || 0) * monthsVacant);
  }

  return {
    vacantSince,
    daysVacant,
    lostRevenue,
    reason,
    lastTenantNames,
  };
}

export function getUpcomingVacancyMetrics(
  unit: UnitDTO | any,
  now: Date = new Date(),
): UpcomingVacancyMetrics {
  const today = startOfDay(now);
  const { contractEndDate } = resolveMostRecentContractEnd(unit, today);

  if (!contractEndDate) {
    return { contractEndDate: null, daysUntilEnd: null };
  }

  const diffMs = contractEndDate.getTime() - today.getTime();
  const days = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  return { contractEndDate, daysUntilEnd: days };
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
